// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Utils
import { getEMRErrorMessage } from "src/utils/emr/emrErrorHelper";

// Types and API
import {
  EMRHubConnectionSourceKind,
  EMRHubConnectionDirection,
} from "../../types";
import { updateEMRHubConnection } from "../../api";

type UpdateEMRHubConnectionPayload = {
  connectionId: string;
  clinicId: string;
  name: string;
  sourceKind: EMRHubConnectionSourceKind;
  direction: EMRHubConnectionDirection;
  configJson: Record<string, unknown>;
  enabled: boolean;
  deviceKey?: null | string;
};

export const useUpdateEMRHubConnection = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async ({
      connectionId,
      ...payload
    }: UpdateEMRHubConnectionPayload) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return updateEMRHubConnection(token, clinicId, connectionId, payload);
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["emr-hub-connections", clinicId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("Connection updated successfully!"),
      });
    },
    onError: (error) => {
      setSnackbarInfo({
        variant: "error",
        message: getEMRErrorMessage(
          error,
          t("Connection could not be updated!"),
          t
        ),
      });
    },
  });
};
